(function () {
  window.ReigningWet = window.ReigningWet || {};

  if (window.ReigningWet.__accountInit) return;
  window.ReigningWet.__accountInit = true;

  var PROFILE_SELECT =
    'id, user_id, username, display_name, avatar_url, bio, creative_role, location_display, website_url, created_at, profile_completed_at, is_banned, doodle_ban_until';

  var state = {
    supabase: null,
    user: null,
    profile: null,
    view: 'sign-in',
    busy: false,
    pendingAuthCallbacks: [],
  };

  var els = {};
  var authChangeListeners = [];

  function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function friendlyError(error) {
    var message = (error && error.message) || '';

    if (/invalid login credentials/i.test(message)) return 'Incorrect email or password.';
    if (/user already registered/i.test(message)) return 'An account with this email already exists.';
    if (/email not confirmed/i.test(message)) return 'Please confirm your email before signing in.';
    if (/password should be at least/i.test(message)) return 'Password must be at least 6 characters.';
    if (/rate limit/i.test(message)) return 'Too many attempts. Please wait a moment and try again.';
    if (/network/i.test(message)) return 'Network error. Check your connection and try again.';

    return 'Something went wrong. Please try again.';
  }

  function generatePlaceholderUsername() {
    return 'creator-' + Math.random().toString(36).slice(2, 8);
  }

  function isSafeImageUrl(url) {
    return typeof url === 'string' && /^https:\/\//i.test(url);
  }

  function isValidHttpUrl(value) {
    try {
      var parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  // Level thresholds and badge ordering live in assets/rw-progression.js,
  // shared with the public profile page so the numbers never drift.
  function getLevelInfo(xp) {
    return window.ReigningWet.getLevelInfo(xp);
  }

  var BADGE_DISPLAY_ORDER = window.ReigningWet.BADGE_ORDER || ['profile_complete', 'first_wave', 'featured'];
  var ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  var MAX_AVATAR_BYTES = 5 * 1024 * 1024;

  // Must match the DB's profiles_creative_role_check constraint exactly —
  // it's a fixed enum, not free text.
  var CREATIVE_ROLES = ['Artist', 'Producer', 'Filmmaker', 'Photographer', 'Designer', 'Creative', 'Other'];

  function cacheEls() {
    els.triggerWrap = document.getElementById('rw-account-trigger');
    els.triggerButton = document.getElementById('rw-account-trigger-button');
    els.triggerAvatar = document.getElementById('rw-account-trigger-avatar');
    els.triggerLabel = document.getElementById('rw-account-trigger-label');
    els.dialog = document.getElementById('rw-account-dialog');
    els.content = document.getElementById('rw-account-content');
  }

  // ---------------------------------------------------------------------
  // Dialog control
  // ---------------------------------------------------------------------

  function openAccount(view) {
    if (!els.dialog) return;
    renderView(view || (state.user ? 'account-summary' : 'sign-in'));
    if (typeof els.dialog.showDialog === 'function') els.dialog.showDialog();
  }

  function closeAccount() {
    if (!els.dialog) return;
    if (typeof els.dialog.closeDialog === 'function') els.dialog.closeDialog();
  }

  // ---------------------------------------------------------------------
  // Trigger UI
  // ---------------------------------------------------------------------

  function updateTriggerUI() {
    if (!els.triggerWrap) return;

    if (state.user) {
      els.triggerWrap.dataset.state = 'signed-in';
      var name = (state.profile && (state.profile.display_name || state.profile.username)) || 'Account';
      if (els.triggerLabel) els.triggerLabel.textContent = name;
      if (els.triggerAvatar) {
        var avatarUrl = state.profile && state.profile.avatar_url;
        els.triggerAvatar.style.backgroundImage = isSafeImageUrl(avatarUrl) ? 'url("' + avatarUrl + '")' : '';
      }
    } else {
      els.triggerWrap.dataset.state = 'signed-out';
      if (els.triggerLabel) els.triggerLabel.textContent = els.triggerLabel.dataset.defaultLabel || 'Account';
      if (els.triggerAvatar) els.triggerAvatar.style.backgroundImage = '';
    }
  }

  // ---------------------------------------------------------------------
  // Profile loading
  // ---------------------------------------------------------------------

  function fetchProfile(userId) {
    return state.supabase
      .from('profiles')
      .select(PROFILE_SELECT)
      .eq('user_id', userId)
      .maybeSingle()
      .then(function (result) {
        if (result.error) throw result.error;
        return result.data;
      });
  }

  function createPlaceholderProfile(userId, attempt) {
    attempt = attempt || 0;
    if (attempt >= 5) {
      return Promise.reject(new Error('Could not create a starter profile. Please refresh and try again.'));
    }

    return state.supabase
      .from('profiles')
      .insert({ user_id: userId, username: generatePlaceholderUsername() })
      .select(PROFILE_SELECT)
      .single()
      .then(function (result) {
        if (result.error) {
          // Unique violation on username — retry with a new random suffix.
          if (result.error.code === '23505') {
            return createPlaceholderProfile(userId, attempt + 1);
          }
          throw result.error;
        }
        return result.data;
      });
  }

  function ensureProfile(userId) {
    return fetchProfile(userId).then(function (profile) {
      if (profile) return profile;
      return createPlaceholderProfile(userId);
    });
  }

  // ---------------------------------------------------------------------
  // Auth state
  // ---------------------------------------------------------------------

  function notifyAuthChange() {
    var detail = { user: state.user, profile: state.profile };
    document.dispatchEvent(new CustomEvent('reigningwet:auth-change', { detail: detail }));
    authChangeListeners.forEach(function (fn) {
      try {
        fn(detail.user, detail.profile);
      } catch (e) {
        if (window.console) console.error(e);
      }
    });
  }

  function handleSession(session, event) {
    var nextUser = (session && session.user) || null;

    if (event === 'PASSWORD_RECOVERY') {
      openAccount('reset-password');
    }

    if (!nextUser) {
      var hadUser = !!state.user;
      state.user = null;
      state.profile = null;
      updateTriggerUI();
      if (hadUser) notifyAuthChange();
      return;
    }

    var isSameUser = state.user && state.user.id === nextUser.id;
    state.user = nextUser;
    updateTriggerUI();

    if (isSameUser) {
      notifyAuthChange();
      return;
    }

    ensureProfile(nextUser.id)
      .then(function (profile) {
        // Server-side (doodle-flag trigger) already enforces this on
        // insert — this just gets a banned user signed out promptly
        // instead of leaving a stale session sitting in the browser.
        if (profile && profile.is_banned) {
          state.user = null;
          state.profile = null;
          updateTriggerUI();
          state.supabase.auth.signOut();
          openAccount('sign-in');
          showMessage('error', 'This account has been suspended.');
          return;
        }

        state.profile = profile;
        updateTriggerUI();
        notifyAuthChange();
        runPendingAuthCallbacks();
        // If a form flow (sign in / sign up) is waiting on this profile to
        // display the summary, render it now that real data is available —
        // otherwise the summary would briefly show placeholder fallback text.
        if (state.view === 'loading') {
          renderView('account-summary');
        }
      })
      .catch(function (error) {
        if (window.console) console.error('[ReigningWet] Failed to load profile:', error);
        notifyAuthChange();
        if (state.view === 'loading') {
          renderView('account-summary');
        }
      });
  }

  function runPendingAuthCallbacks() {
    var callbacks = state.pendingAuthCallbacks;
    state.pendingAuthCallbacks = [];
    callbacks.forEach(function (fn) {
      try {
        fn(state.user, state.profile);
      } catch (e) {
        if (window.console) console.error(e);
      }
    });
  }

  function initAuth() {
    state.supabase = window.ReigningWet.supabase;

    state.supabase.auth
      .getSession()
      .then(function (result) {
        handleSession(result.data && result.data.session);
      })
      .catch(function (error) {
        if (window.console) console.error('[ReigningWet] getSession failed:', error);
        updateTriggerUI();
      });

    state.supabase.auth.onAuthStateChange(function (event, session) {
      handleSession(session, event);
    });
  }

  // ---------------------------------------------------------------------
  // Profile editing, XP/badges, avatar upload
  // ---------------------------------------------------------------------

  function saveProfile(userId, payload) {
    return state.supabase
      .from('profiles')
      .update(payload)
      .eq('user_id', userId)
      .select(PROFILE_SELECT)
      .single()
      .then(function (result) {
        if (result.error) throw result.error;
        return result.data;
      });
  }

  function completeProfileIfEligible() {
    // Idempotent on the backend (dedup_key + profile_completed_at guard) —
    // safe to call after every save, never awards XP/badges twice.
    //
    // Supabase's query/RPC builders are thenables (they implement .then()
    // to support chaining) but not full Promises, so they have no .catch()
    // method — calling one directly throws. They also resolve normally
    // with { data, error } on a database-level failure rather than
    // rejecting, so the error has to be checked explicitly here rather
    // than relying on rejection at all. Never lets a failure here reject
    // the caller's chain — this must not block the surrounding save flow.
    return state.supabase.rpc('complete_profile').then(
      function (result) {
        if (result && result.error && window.console) {
          console.error('[ReigningWet] complete_profile RPC failed:', result.error);
        }
      },
      function (error) {
        if (window.console) console.error('[ReigningWet] complete_profile RPC failed:', error);
      }
    );
  }

  function fetchXpTotal(userId) {
    return state.supabase
      .from('xp_transactions')
      .select('amount')
      .eq('user_id', userId)
      .then(function (result) {
        if (result.error) throw result.error;
        return (result.data || []).reduce(function (sum, row) {
          return sum + (row.amount || 0);
        }, 0);
      });
  }

  function fetchEarnedBadges(userId) {
    return Promise.all([
      state.supabase.from('badges').select('id, slug, name, description'),
      state.supabase.from('user_badges').select('badge_id').eq('user_id', userId),
    ]).then(function (results) {
      var badgesResult = results[0];
      var userBadgesResult = results[1];
      if (badgesResult.error) throw badgesResult.error;
      if (userBadgesResult.error) throw userBadgesResult.error;

      var earnedIds = {};
      (userBadgesResult.data || []).forEach(function (row) {
        earnedIds[row.badge_id] = true;
      });

      return (badgesResult.data || [])
        .filter(function (badge) {
          return earnedIds[badge.id];
        })
        .sort(function (a, b) {
          return BADGE_DISPLAY_ORDER.indexOf(a.slug) - BADGE_DISPLAY_ORDER.indexOf(b.slug);
        });
    });
  }

  function renderXpBlock(xpInfo, badges) {
    var el = document.getElementById('rw-account-xp-block');
    if (!el) return;

    var pct = Math.round(xpInfo.progress * 100);
    var nextLabel = xpInfo.nextName
      ? xpInfo.xp + ' / ' + xpInfo.nextMinXp + ' XP to ' + escapeHtml(xpInfo.nextName)
      : 'Max level reached';

    var badgesHtml = badges.length
      ? badges
          .map(function (badge) {
            return (
              '<div class="rw-account-badge" title="' +
              escapeHtml(badge.description || '') +
              '">' +
              escapeHtml(badge.name) +
              '</div>'
            );
          })
          .join('')
      : '<div class="rw-account-badge rw-account-badge--empty">No badges yet</div>';

    el.innerHTML =
      '<div class="rw-account-level-row"><span class="rw-account-level-name">' +
      escapeHtml(xpInfo.name) +
      '</span><span class="rw-account-level-xp">' +
      xpInfo.xp +
      ' XP</span></div>' +
      '<div class="rw-account-progress-track"><div class="rw-account-progress-fill" style="width:' +
      pct +
      '%"></div></div>' +
      '<div class="rw-account-level-next">' +
      nextLabel +
      '</div>' +
      '<div class="rw-account-badges">' +
      badgesHtml +
      '</div>';
  }

  function loadAccountExtras() {
    if (!state.user) return;
    var userId = state.user.id;

    Promise.all([fetchXpTotal(userId), fetchEarnedBadges(userId)])
      .then(function (results) {
        renderXpBlock(getLevelInfo(results[0]), results[1]);
      })
      .catch(function (error) {
        var el = document.getElementById('rw-account-xp-block');
        if (el) el.innerHTML = '<p class="rw-account-subtext" style="margin:0;">Could not load XP right now.</p>';
        if (window.console) console.error('[ReigningWet] Failed to load XP/badges:', error);
      });
  }

  var SUBMISSION_STATUS_LABEL = {
    pending: 'Pending review',
    approved: 'Approved',
    rejected: 'Rejected',
  };

  function fetchMySubmissions(userId) {
    return state.supabase
      .from('submissions')
      .select('id, project_title, project_url, category, location_display, status, is_featured, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(function (result) {
        if (result.error) throw result.error;
        return result.data || [];
      });
  }

  function renderSubmissionsList(submissions) {
    var el = document.getElementById('rw-account-submissions-list');
    if (!el) return;

    if (!submissions.length) {
      el.innerHTML = '<p class="rw-account-subtext" style="margin:0;">You haven’t shared a wave yet.</p>';
      return;
    }

    el.innerHTML = submissions
      .map(function (submission) {
        var statusLabel = SUBMISSION_STATUS_LABEL[submission.status] || submission.status;
        return (
          '<div class="rw-account-submission">' +
          '<div class="rw-account-submission-title">' +
          escapeHtml(submission.project_title) +
          (submission.is_featured ? ' <span class="rw-account-badge" style="padding:2px 7px;">Featured</span>' : '') +
          '</div>' +
          '<div class="rw-account-submission-meta">' +
          escapeHtml(submission.category || 'Other') +
          (submission.location_display ? ' · ' + escapeHtml(submission.location_display) : '') +
          '</div>' +
          '<div class="rw-account-submission-status rw-account-submission-status--' +
          escapeHtml(submission.status) +
          '">' +
          escapeHtml(statusLabel) +
          '</div>' +
          '</div>'
        );
      })
      .join('');
  }

  function loadMySubmissions() {
    if (!state.user) return;
    var el = document.getElementById('rw-account-submissions-list');
    if (el) {
      el.innerHTML =
        '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>';
    }

    fetchMySubmissions(state.user.id)
      .then(renderSubmissionsList)
      .catch(function (error) {
        if (el) el.innerHTML = '<p class="rw-account-subtext" style="margin:0;">Could not load your submissions right now.</p>';
        if (window.console) console.error('[ReigningWet] Failed to load submissions:', error);
      });
  }

  function fetchMyOrders(userId) {
    return state.supabase
      .from('orders')
      .select(
        'id, order_number, financial_status, fulfillment_status, total_price, currency, line_items, tracking_number, tracking_url, order_status_url, shopify_created_at'
      )
      .eq('user_id', userId)
      .order('shopify_created_at', { ascending: false })
      .then(function (result) {
        if (result.error) throw result.error;
        return result.data || [];
      });
  }

  // Orders carry two independent status dimensions (payment + fulfillment);
  // this collapses them into the one pill a user actually needs to see,
  // reusing the same visual states as submission status (approved/pending/
  // rejected) so no new styling is needed.
  function orderStatusInfo(order) {
    if (order.fulfillment_status === 'fulfilled') return { label: 'Shipped', modifier: 'approved' };
    if (order.fulfillment_status === 'partial') return { label: 'Partially shipped', modifier: 'pending' };
    if (order.financial_status === 'refunded' || order.financial_status === 'partially_refunded') {
      return { label: 'Refunded', modifier: 'rejected' };
    }
    if (order.financial_status === 'paid') return { label: 'Processing', modifier: 'pending' };
    if (order.financial_status === 'pending') return { label: 'Payment pending', modifier: 'pending' };
    return { label: order.financial_status || 'Unknown', modifier: 'pending' };
  }

  function formatMoney(amount, currency) {
    if (amount === null || amount === undefined) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return amount + ' ' + (currency || '');
    }
  }

  function formatOrderDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function lineItemsSummary(lineItems) {
    if (!lineItems || !lineItems.length) return '';
    var first = lineItems[0].title || 'Item';
    return lineItems.length > 1 ? first + ' +' + (lineItems.length - 1) + ' more' : first;
  }

  function renderOrdersList(orders) {
    var el = document.getElementById('rw-account-orders-list');
    if (!el) return;

    if (!orders.length) {
      el.innerHTML = '<p class="rw-account-subtext" style="margin:0;">No orders yet.</p>';
      return;
    }

    el.innerHTML = orders
      .map(function (order) {
        var status = orderStatusInfo(order);
        var trackingLink =
          order.tracking_url && isValidHttpUrl(order.tracking_url)
            ? '<a class="rw-account-link" href="' +
              escapeHtml(order.tracking_url) +
              '" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:6px;">Track package →</a>'
            : '';

        return (
          '<div class="rw-account-submission">' +
          '<div class="rw-account-submission-title">' +
          escapeHtml(order.order_number || 'Order') +
          '</div>' +
          '<div class="rw-account-submission-meta">' +
          escapeHtml(formatOrderDate(order.shopify_created_at)) +
          ' · ' +
          escapeHtml(lineItemsSummary(order.line_items)) +
          ' · ' +
          escapeHtml(formatMoney(order.total_price, order.currency)) +
          '</div>' +
          '<div class="rw-account-submission-status rw-account-submission-status--' +
          status.modifier +
          '">' +
          escapeHtml(status.label) +
          '</div>' +
          trackingLink +
          '</div>'
        );
      })
      .join('');
  }

  function loadMyOrders() {
    if (!state.user) return;
    var el = document.getElementById('rw-account-orders-list');
    if (el) {
      el.innerHTML =
        '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>';
    }

    fetchMyOrders(state.user.id)
      .then(renderOrdersList)
      .catch(function (error) {
        if (el) el.innerHTML = '<p class="rw-account-subtext" style="margin:0;">Could not load your orders right now.</p>';
        if (window.console) console.error('[ReigningWet] Failed to load orders:', error);
      });
  }

  function handleAvatarUpload(file) {
    var statusEl = document.getElementById('rw-avatar-status');
    var previewEl = document.getElementById('rw-avatar-preview');

    if (ALLOWED_AVATAR_TYPES.indexOf(file.type) === -1) {
      if (statusEl) statusEl.textContent = 'Please choose a JPEG, PNG, or WebP image.';
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      if (statusEl) statusEl.textContent = 'Image must be smaller than 5MB.';
      return;
    }

    if (statusEl) statusEl.textContent = 'Uploading…';

    var localPreviewUrl = URL.createObjectURL(file);
    if (previewEl) previewEl.style.backgroundImage = 'url("' + localPreviewUrl + '")';

    var extMatch = /\.([a-zA-Z0-9]+)$/.exec(file.name);
    var ext = extMatch
      ? extMatch[1].toLowerCase()
      : file.type === 'image/png'
        ? 'png'
        : file.type === 'image/webp'
          ? 'webp'
          : 'jpg';
    var path = state.user.id + '/avatar-' + Date.now() + '.' + ext;

    state.supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type })
      .then(function (result) {
        if (result.error) throw result.error;
        var publicUrlResult = state.supabase.storage.from('avatars').getPublicUrl(path);
        var publicUrl = publicUrlResult.data && publicUrlResult.data.publicUrl;
        if (!publicUrl) throw new Error('Could not resolve uploaded image URL');
        return saveProfile(state.user.id, { avatar_url: publicUrl });
      })
      .then(function (profile) {
        URL.revokeObjectURL(localPreviewUrl);
        state.profile = profile;
        updateTriggerUI();
        notifyAuthChange();
        if (statusEl) statusEl.textContent = 'Photo updated.';
        return completeProfileIfEligible();
      })
      .catch(function (error) {
        URL.revokeObjectURL(localPreviewUrl);
        if (statusEl) statusEl.textContent = friendlyError(error);
        if (window.console) console.error('[ReigningWet] Avatar upload failed:', error);
      });
  }

  // ---------------------------------------------------------------------
  // View templates
  // ---------------------------------------------------------------------

  function messageMarkup() {
    return '<div class="rw-account-message" id="rw-account-message"></div>';
  }

  function showMessage(type, text) {
    var el = document.getElementById('rw-account-message');
    if (!el) return;
    el.textContent = text;
    el.className = 'rw-account-message rw-account-message--visible rw-account-message--' + type;
  }

  function clearMessage() {
    var el = document.getElementById('rw-account-message');
    if (!el) return;
    el.className = 'rw-account-message';
    el.textContent = '';
  }

  function setBusy(button, busy, busyLabel, idleLabel) {
    state.busy = busy;
    if (!button) return;
    button.disabled = busy;
    button.textContent = busy ? busyLabel : idleLabel;
  }

  var templates = {
    'sign-in': function () {
      return (
        '<div class="rw-account-eyebrow">the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Sign In</h2>' +
        '<p class="rw-account-subtext">Welcome back. Sign in to your Reigning Wet account.</p>' +
        '<form class="rw-account-form" id="rw-account-form-sign-in">' +
        '<div class="rw-account-field"><label for="rw-si-email">Email</label><input type="email" id="rw-si-email" autocomplete="email" required></div>' +
        '<div class="rw-account-field"><label for="rw-si-password">Password</label><input type="password" id="rw-si-password" autocomplete="current-password" required></div>' +
        '<button type="submit" class="rw-account-submit" id="rw-account-submit">Sign In</button>' +
        messageMarkup() +
        '</form>' +
        '<div class="rw-account-links">' +
        '<button type="button" class="rw-account-link" data-view="forgot-password">Forgot password?</button>' +
        '<button type="button" class="rw-account-link" data-view="sign-up">Create account</button>' +
        '</div>'
      );
    },

    'sign-up': function () {
      return (
        '<div class="rw-account-eyebrow">join the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Create Account</h2>' +
        '<p class="rw-account-subtext">Just an email and password to start. You can build your creator profile after.</p>' +
        '<form class="rw-account-form" id="rw-account-form-sign-up">' +
        '<div class="rw-account-field"><label for="rw-su-email">Email</label><input type="email" id="rw-su-email" autocomplete="email" required></div>' +
        '<div class="rw-account-field"><label for="rw-su-password">Password</label><input type="password" id="rw-su-password" autocomplete="new-password" minlength="8" required></div>' +
        '<div class="rw-account-field"><label for="rw-su-password-confirm">Confirm Password</label><input type="password" id="rw-su-password-confirm" autocomplete="new-password" minlength="8" required></div>' +
        '<button type="submit" class="rw-account-submit" id="rw-account-submit">Create Account</button>' +
        messageMarkup() +
        '</form>' +
        '<div class="rw-account-links">' +
        '<span></span>' +
        '<button type="button" class="rw-account-link" data-view="sign-in">Already have an account? Sign in</button>' +
        '</div>'
      );
    },

    'sign-up-check-email': function (opts) {
      return (
        '<div class="rw-account-eyebrow">almost there</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Check Your Email</h2>' +
        '<p class="rw-account-subtext">We sent a confirmation link to <strong>' +
        escapeHtml(opts.email) +
        '</strong>. Click it to activate your account, then come back and sign in.</p>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="sign-in">Back to sign in</button></div>'
      );
    },

    'forgot-password': function () {
      return (
        '<div class="rw-account-eyebrow">the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Reset Password</h2>' +
        '<p class="rw-account-subtext">Enter your email and we’ll send you a link to reset your password.</p>' +
        '<form class="rw-account-form" id="rw-account-form-forgot-password">' +
        '<div class="rw-account-field"><label for="rw-fp-email">Email</label><input type="email" id="rw-fp-email" autocomplete="email" required></div>' +
        '<button type="submit" class="rw-account-submit" id="rw-account-submit">Send Reset Link</button>' +
        messageMarkup() +
        '</form>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="sign-in">Back to sign in</button></div>'
      );
    },

    'forgot-password-sent': function (opts) {
      return (
        '<div class="rw-account-eyebrow">the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Check Your Email</h2>' +
        '<p class="rw-account-subtext">If an account exists for <strong>' +
        escapeHtml(opts.email) +
        '</strong>, a password reset link is on its way.</p>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="sign-in">Back to sign in</button></div>'
      );
    },

    'reset-password': function () {
      return (
        '<div class="rw-account-eyebrow">the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Set New Password</h2>' +
        '<p class="rw-account-subtext">Choose a new password for your account.</p>' +
        '<form class="rw-account-form" id="rw-account-form-reset-password">' +
        '<div class="rw-account-field"><label for="rw-rp-password">New Password</label><input type="password" id="rw-rp-password" autocomplete="new-password" minlength="8" required></div>' +
        '<div class="rw-account-field"><label for="rw-rp-password-confirm">Confirm Password</label><input type="password" id="rw-rp-password-confirm" autocomplete="new-password" minlength="8" required></div>' +
        '<button type="submit" class="rw-account-submit" id="rw-account-submit">Update Password</button>' +
        messageMarkup() +
        '</form>'
      );
    },

    'reset-password-done': function () {
      return (
        '<div class="rw-account-eyebrow">the current</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Password Updated</h2>' +
        '<p class="rw-account-subtext">Your password has been changed. You’re signed in.</p>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="account-summary">Continue</button></div>'
      );
    },

    'account-summary': function () {
      var name = (state.profile && (state.profile.display_name || state.profile.username)) || 'Creator';
      var avatarUrl = state.profile && state.profile.avatar_url;
      var avatarStyle = isSafeImageUrl(avatarUrl) ? ' style="background-image:url(' + escapeHtml(avatarUrl) + ')"' : '';

      return (
        '<div class="rw-account-summary">' +
        '<div class="rw-account-summary__avatar"' +
        avatarStyle +
        '></div>' +
        '<div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading" style="font-size:18px;margin:0;">' +
        escapeHtml(name) +
        '</h2>' +
        '<div class="rw-account-summary__email">' +
        escapeHtml((state.user && state.user.email) || '') +
        '</div>' +
        '</div>' +
        '</div>' +
        '<div class="rw-account-xp-block" id="rw-account-xp-block">' +
        '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>' +
        '</div>' +
        '<button type="button" class="rw-account-submit" id="rw-account-edit-profile">Edit Profile</button>' +
        '<button type="button" class="rw-account-submit" id="rw-account-view-submissions" style="background:transparent;color:#4fd68c;border:1px solid rgba(79,214,140,0.4);margin-top:8px;">My Submissions</button>' +
        '<button type="button" class="rw-account-submit" id="rw-account-view-orders" style="background:transparent;color:#4fd68c;border:1px solid rgba(79,214,140,0.4);margin-top:8px;">My Orders</button>' +
        '<button type="button" class="rw-account-link" id="rw-account-sign-out" style="display:block;margin:14px auto 0;">Sign Out</button>' +
        messageMarkup()
      );
    },

    'my-submissions': function () {
      return (
        '<div class="rw-account-eyebrow">wetworldwav</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">My Submissions</h2>' +
        '<div id="rw-account-submissions-list">' +
        '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>' +
        '</div>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="account-summary">Back</button></div>'
      );
    },

    'my-orders': function () {
      return (
        '<div class="rw-account-eyebrow">the roster</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">My Orders</h2>' +
        '<div id="rw-account-orders-list">' +
        '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>' +
        '</div>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="account-summary">Back</button></div>'
      );
    },

    'profile-edit': function () {
      var p = state.profile || {};
      var avatarUrl = p.avatar_url;
      var avatarStyle = isSafeImageUrl(avatarUrl) ? ' style="background-image:url(' + escapeHtml(avatarUrl) + ')"' : '';

      return (
        '<div class="rw-account-eyebrow">your profile</div>' +
        '<h2 class="rw-account-heading" id="rw-account-heading">Edit Profile</h2>' +
        '<div class="rw-account-avatar-row">' +
        '<div class="rw-account-avatar-preview" id="rw-avatar-preview"' +
        avatarStyle +
        '></div>' +
        '<div>' +
        '<label class="rw-account-link" for="rw-avatar-input" style="cursor:pointer;">Change Photo</label>' +
        '<input type="file" id="rw-avatar-input" accept="image/png,image/jpeg,image/webp" style="display:none">' +
        '<div class="rw-account-subtext" id="rw-avatar-status" style="margin:4px 0 0;font-size:11px;"></div>' +
        '</div>' +
        '</div>' +
        '<form class="rw-account-form" id="rw-account-form-profile-edit">' +
        '<div class="rw-account-field"><label for="rw-pe-username">Username</label><input type="text" id="rw-pe-username" maxlength="20" value="' +
        escapeHtml(p.username || '') +
        '" required></div>' +
        '<div class="rw-account-field"><label for="rw-pe-display-name">Display Name</label><input type="text" id="rw-pe-display-name" maxlength="60" value="' +
        escapeHtml(p.display_name || '') +
        '"></div>' +
        '<div class="rw-account-field"><label for="rw-pe-bio">Short Bio</label><textarea id="rw-pe-bio" maxlength="280" rows="3">' +
        escapeHtml(p.bio || '') +
        '</textarea></div>' +
        '<div class="rw-account-field"><label for="rw-pe-role">Creative Role</label><select id="rw-pe-role">' +
        '<option value=""' +
        (!p.creative_role ? ' selected' : '') +
        '>Select a role</option>' +
        CREATIVE_ROLES.map(function (role) {
          return (
            '<option value="' +
            escapeHtml(role) +
            '"' +
            (p.creative_role === role ? ' selected' : '') +
            '>' +
            escapeHtml(role) +
            '</option>'
          );
        }).join('') +
        '</select></div>' +
        '<div class="rw-account-field"><label for="rw-pe-location">Location (optional)</label><input type="text" id="rw-pe-location" maxlength="60" value="' +
        escapeHtml(p.location_display || '') +
        '"></div>' +
        '<div class="rw-account-field"><label for="rw-pe-website">Website / Social Link (optional)</label><input type="url" id="rw-pe-website" maxlength="200" value="' +
        escapeHtml(p.website_url || '') +
        '" placeholder="https://"></div>' +
        '<button type="submit" class="rw-account-submit" id="rw-account-submit">Save Profile</button>' +
        messageMarkup() +
        '</form>' +
        '<div class="rw-account-links"><span></span><button type="button" class="rw-account-link" data-view="account-summary">Back</button></div>'
      );
    },

    loading: function () {
      return '<div class="rw-account-loading"><div class="rw-account-loading-dots"><span></span><span></span><span></span></div></div>';
    },
  };

  function bindViewEvents(view) {
    if (!els.content) return;

    els.content.querySelectorAll('[data-view]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        renderView(btn.dataset.view);
      });
    });

    if (view === 'sign-in') {
      var form = document.getElementById('rw-account-form-sign-in');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearMessage();
        var email = document.getElementById('rw-si-email').value.trim();
        var password = document.getElementById('rw-si-password').value;
        var button = document.getElementById('rw-account-submit');
        setBusy(button, true, 'Signing In…', 'Sign In');

        state.supabase.auth
          .signInWithPassword({ email: email, password: password })
          .then(function (result) {
            setBusy(button, false, 'Signing In…', 'Sign In');
            if (result.error) {
              showMessage('error', friendlyError(result.error));
              return;
            }
            // Show a brief loading state instead of the summary directly —
            // the profile row may still be loading/being created, and
            // handleSession() will swap to account-summary once it's ready.
            renderView('loading');
          })
          .catch(function (error) {
            setBusy(button, false, 'Signing In…', 'Sign In');
            showMessage('error', friendlyError(error));
          });
      });
    }

    if (view === 'sign-up') {
      var suForm = document.getElementById('rw-account-form-sign-up');
      suForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearMessage();
        var email = document.getElementById('rw-su-email').value.trim();
        var password = document.getElementById('rw-su-password').value;
        var confirm = document.getElementById('rw-su-password-confirm').value;
        var button = document.getElementById('rw-account-submit');

        if (password.length < 8) {
          showMessage('error', 'Password must be at least 8 characters.');
          return;
        }
        if (password !== confirm) {
          showMessage('error', 'Passwords do not match.');
          return;
        }

        setBusy(button, true, 'Creating Account…', 'Create Account');

        state.supabase.auth
          .signUp({
            email: email,
            password: password,
            options: { emailRedirectTo: window.location.origin + '/' },
          })
          .then(function (result) {
            setBusy(button, false, 'Creating Account…', 'Create Account');
            if (result.error) {
              showMessage('error', friendlyError(result.error));
              return;
            }
            if (result.data && result.data.session) {
              renderView('loading');
            } else {
              renderView('sign-up-check-email', { email: email });
            }
          })
          .catch(function (error) {
            setBusy(button, false, 'Creating Account…', 'Create Account');
            showMessage('error', friendlyError(error));
          });
      });
    }

    if (view === 'forgot-password') {
      var fpForm = document.getElementById('rw-account-form-forgot-password');
      fpForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearMessage();
        var email = document.getElementById('rw-fp-email').value.trim();
        var button = document.getElementById('rw-account-submit');
        setBusy(button, true, 'Sending…', 'Send Reset Link');

        state.supabase.auth
          .resetPasswordForEmail(email, { redirectTo: window.location.origin + '/' })
          .then(function (result) {
            setBusy(button, false, 'Sending…', 'Send Reset Link');
            // Intentionally treat as success even on error to avoid leaking which emails exist.
            renderView('forgot-password-sent', { email: email });
          })
          .catch(function () {
            setBusy(button, false, 'Sending…', 'Send Reset Link');
            renderView('forgot-password-sent', { email: email });
          });
      });
    }

    if (view === 'reset-password') {
      var rpForm = document.getElementById('rw-account-form-reset-password');
      rpForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearMessage();
        var password = document.getElementById('rw-rp-password').value;
        var confirm = document.getElementById('rw-rp-password-confirm').value;
        var button = document.getElementById('rw-account-submit');

        if (password.length < 8) {
          showMessage('error', 'Password must be at least 8 characters.');
          return;
        }
        if (password !== confirm) {
          showMessage('error', 'Passwords do not match.');
          return;
        }

        setBusy(button, true, 'Updating…', 'Update Password');

        state.supabase.auth
          .updateUser({ password: password })
          .then(function (result) {
            setBusy(button, false, 'Updating…', 'Update Password');
            if (result.error) {
              showMessage('error', friendlyError(result.error));
              return;
            }
            renderView('reset-password-done');
          })
          .catch(function (error) {
            setBusy(button, false, 'Updating…', 'Update Password');
            showMessage('error', friendlyError(error));
          });
      });
    }

    if (view === 'account-summary') {
      var signOutBtn = document.getElementById('rw-account-sign-out');
      signOutBtn.addEventListener('click', function () {
        signOut();
      });

      var editBtn = document.getElementById('rw-account-edit-profile');
      editBtn.addEventListener('click', function () {
        renderView('profile-edit');
      });

      var viewSubmissionsBtn = document.getElementById('rw-account-view-submissions');
      viewSubmissionsBtn.addEventListener('click', function () {
        renderView('my-submissions');
      });

      var viewOrdersBtn = document.getElementById('rw-account-view-orders');
      viewOrdersBtn.addEventListener('click', function () {
        renderView('my-orders');
      });

      loadAccountExtras();
    }

    if (view === 'my-submissions') {
      loadMySubmissions();
    }

    if (view === 'my-orders') {
      loadMyOrders();
    }

    if (view === 'profile-edit') {
      var peForm = document.getElementById('rw-account-form-profile-edit');
      peForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearMessage();

        var username = document.getElementById('rw-pe-username').value.trim();
        var displayName = document.getElementById('rw-pe-display-name').value.trim();
        var bio = document.getElementById('rw-pe-bio').value.trim();
        var role = document.getElementById('rw-pe-role').value.trim();
        var location = document.getElementById('rw-pe-location').value.trim();
        var website = document.getElementById('rw-pe-website').value.trim();
        var button = document.getElementById('rw-account-submit');

        if (!/^[a-zA-Z0-9_-]{3,20}$/.test(username)) {
          showMessage('error', 'Username must be 3-20 characters: letters, numbers, _ or - only.');
          return;
        }
        if (website && !isValidHttpUrl(website)) {
          showMessage('error', 'Website link must be a valid http:// or https:// URL.');
          return;
        }

        var payload = {
          username: username,
          display_name: displayName || null,
          bio: bio || null,
          creative_role: role || null,
          location_display: location || null,
          website_url: website || null,
        };

        setBusy(button, true, 'Saving…', 'Save Profile');

        saveProfile(state.user.id, payload)
          .then(function (profile) {
            state.profile = profile;
            updateTriggerUI();
            notifyAuthChange();
            return completeProfileIfEligible();
          })
          .then(function () {
            setBusy(button, false, 'Saving…', 'Save Profile');
            renderView('account-summary');
          })
          .catch(function (error) {
            setBusy(button, false, 'Saving…', 'Save Profile');
            if (window.console) console.error('[ReigningWet] Profile save failed:', error);
            if (error && error.code === '23505') {
              showMessage('error', 'That username is already taken.');
            } else if (error && error.code === '23514') {
              showMessage('error', 'One of the fields has an invalid value. Please check your entries and try again.');
            } else {
              showMessage('error', friendlyError(error));
            }
          });
      });

      var avatarInput = document.getElementById('rw-avatar-input');
      avatarInput.addEventListener('change', function () {
        var file = avatarInput.files && avatarInput.files[0];
        if (file) handleAvatarUpload(file);
      });
    }
  }

  function renderView(view, opts) {
    state.view = view;
    if (!els.content) return;
    els.content.innerHTML = templates[view] ? templates[view](opts || {}) : templates['sign-in']();
    bindViewEvents(view);
  }

  function signOut() {
    var summary = document.getElementById('rw-account-content');
    if (summary) renderView('loading');
    state.supabase.auth.signOut().then(function () {
      closeAccount();
    });
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------

  window.ReigningWet.openAccount = openAccount;
  window.ReigningWet.closeAccount = closeAccount;
  window.ReigningWet.getUser = function () {
    return state.user;
  };
  window.ReigningWet.getProfile = function () {
    return state.profile;
  };
  window.ReigningWet.signOut = signOut;
  window.ReigningWet.onAuthChange = function (fn) {
    if (typeof fn === 'function') authChangeListeners.push(fn);
  };
  window.ReigningWet.requireAuth = function (callback) {
    if (state.user) {
      callback(state.user, state.profile);
      return;
    }
    state.pendingAuthCallbacks.push(callback);
    openAccount('sign-in');
  };
  // Single source of truth for the public profile URL pattern, so future
  // integrations (e.g. linking creator names from The Current) stay consistent.
  window.ReigningWet.getProfileUrl = function (username) {
    return '/pages/profile?u=' + encodeURIComponent(username);
  };

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  function boot() {
    cacheEls();
    if (els.triggerLabel) els.triggerLabel.dataset.defaultLabel = els.triggerLabel.textContent;

    if (els.triggerButton) {
      els.triggerButton.addEventListener('click', function () {
        openAccount();
      });
    }

    // The account panel reuses the theme's dialog-component, which closes
    // itself on any click it detects as "outside" the dialog. That check
    // runs on a delegated click listener attached to the dialog wrapper,
    // so it sees every click that bubbles up from inside our own content
    // — including clicks on our own buttons/forms — before our submit
    // handlers even run. Stopping propagation here, once, on the stable
    // content container (its innerHTML is replaced on every renderView()
    // call, but this element itself never is) keeps every click and
    // submit inside the panel from ever reaching that listener.
    if (els.content) {
      els.content.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    if (window.ReigningWet.supabase) {
      initAuth();
    } else {
      document.addEventListener('reigningwet:supabase-ready', initAuth, { once: true });
      document.addEventListener(
        'reigningwet:supabase-error',
        function () {
          updateTriggerUI();
        },
        { once: true }
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
